import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import { CloseRounded } from "@mui/icons-material";
import color from "../../../Constants/colors";
import {
  DocumentCraftingDocumentDetail,
  DocumentCraftingUser,
} from "../../../Models/documentCrafting";
import { useUpdateDocumentCraftingDocument } from "../../../Query/useDocumentCrafting";
import { displayName, useUserName } from "../Common/format";
import MemberSearch from "./MemberSearch";

interface Props {
  open: boolean;
  document: DocumentCraftingDocumentDetail;
  onClose: () => void;
}

/** Owner, workspace admin and platform admin hand the document to another
 * member of the workspace (`PATCH {owner_id}`). */
const TransferOwnershipDialog: React.FC<Props> = ({ open, document, onClose }) => {
  const { t } = useTranslation("documentCrafting");
  const userName = useUserName();
  const update = useUpdateDocumentCraftingDocument();
  const [owner, setOwner] = useState<DocumentCraftingUser | null>(null);

  const transfer = () => {
    if (!owner) return;
    update.mutate(
      { documentId: document.id, data: { owner_id: owner.id } },
      { onSuccess: onClose },
    );
  };

  return (
    <Dialog
      open={open}
      onClose={update.isPending ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      TransitionProps={{ onExited: () => setOwner(null) }}
    >
      <DialogTitle>{t("detail.transfer.title")}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }}>
          {t("detail.transfer.current", { name: userName(document.owner) })}
        </Typography>
        {owner ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              p: 1.25,
              borderRadius: "10px",
              border: `1px solid ${color.gray200}`,
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontSize: 14, fontWeight: 600, color: color.gray900 }}>
                {displayName(owner)}
              </Typography>
              <Typography sx={{ fontSize: 12, color: color.gray500 }}>
                {owner.email}
              </Typography>
            </Box>
            <IconButton
              size="small"
              onClick={() => setOwner(null)}
              aria-label={t("detail.transfer.change")}
              disabled={update.isPending}
            >
              <CloseRounded fontSize="small" />
            </IconButton>
          </Box>
        ) : (
          <MemberSearch
            label={t("detail.transfer.newOwner")}
            excludeIds={document.owner ? [document.owner.id] : []}
            onSelect={setOwner}
            autoFocus
          />
        )}
        {owner && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            {t("detail.transfer.warning", {
              name: displayName(owner),
              title: document.title,
            })}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button color="secondary" onClick={onClose} disabled={update.isPending}>
          {t("common.cancel")}
        </Button>
        <Button
          variant="contained"
          onClick={transfer}
          disabled={!owner || update.isPending}
        >
          {t("detail.transfer.submit")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TransferOwnershipDialog;
